import { z } from 'zod';
import { GAME_IDS, zIdempotencyKey, type GameId } from './common';
import type { GameEconomy } from './catalog';
import type { LedgerKind } from './wallet';
import { HISTORY_GAME_FILTERS, type BetHistoryItem } from './history';
import type { LimitsState } from './limits';
import type { Season } from './season';
import type { ChatReportReason, ChatReportStatus } from './chat';

/** Every mutating admin action needs a human comment (stored in the AdminAction audit log). */
export const zAdminComment = z.string().trim().min(3).max(500);

export const zPage = z.coerce.number().int().min(1).max(100_000).default(1);

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * GET /api/admin/users?q=&page= — q matches any part of the e-mail / nickname (case-insensitive; one character —
 * the beginning only, from 2 — anywhere, pg_trgm index) or the public ID (`123` / `#123`).
 */
export const zAdminUserQuery = z.object({ q: z.string().trim().max(100).optional(), page: zPage });
/** shorter queries match the beginning of the e-mail / nickname only */
export const ADMIN_SEARCH_MIN_SUBSTRING = 2;

export interface AdminUserRow {
  id: string;
  publicId: number;
  /** null: signed up with Telegram, no address added yet */
  email: string | null;
  nickname: string;
  role: 'user' | 'admin';
  balance: number;
  level: number;
  bannedAt: string | null;
  createdAt: string;
  /** account deletion: pending (can be cancelled until `deleteAfter`) or done (erased); null = none */
  deletion: AdminDeletionState | null;
}

export interface AdminDeletionState {
  status: 'pending' | 'deleted';
  requestedAt: string | null;
  deleteAfter: string | null;
  deletedAt: string | null;
}

/** POST /api/admin/users/:id/deletion/cancel — cancel a pending deletion at the player's request (audited) */
export const zAdminDeletionCancel = z.object({ comment: zAdminComment }).strict();

export interface AdminUserCard extends AdminUserRow {
  lastSeenAt: string | null;
  invitedBy: string | null;
  stats: { bets: number; staked: number; paidOut: number };
  limits: { tokensPerDay: number | null; minutesPerDay: number | null; breakUntil: string | null };
  /** Σ ledger = cached balance (should always be true) */
  ledgerOk: boolean;
}

export const zAdminLedgerQuery = z.object({ page: zPage });

export interface AdminLedgerRow {
  id: string;
  kind: LedgerKind;
  amount: number;
  balanceAfter: number;
  game: string | null;
  refId: string | null;
  meta: unknown;
  createdAt: string;
}

/** POST /api/admin/users/:id/adjust — signed amount (tokens), goes through the ledger as `admin_adjust` */
export const zAdminAdjust = z
  .object({
    amount: z
      .number()
      .int()
      .min(-1_000_000_000)
      .max(1_000_000_000)
      .refine((n) => n !== 0, { message: 'amount must not be 0' }),
    comment: zAdminComment,
    idempotencyKey: zIdempotencyKey,
  })
  .strict();
export type AdminAdjustInput = z.input<typeof zAdminAdjust>;

/* ---------------- token grants («Начислить токены») ---------------- */

/** grant presets in the admin panel (tokens) */
export const ADMIN_GRANT_PRESETS = [100, 1_000, 10_000] as const;
/** players per bulk grant request */
export const ADMIN_GRANT_MAX_USERS = 500;
/** default cap of one grant per player (tokens); the server may lower it in its config (ADMIN_GRANT_MAX) */
export const ADMIN_GRANT_MAX_AMOUNT = 1_000_000;

/** one line of the bulk list: e-mail, nickname or public ID (`#123` / `123`) */
const zGrantTarget = z.string().trim().min(1).max(100);

/** POST /api/admin/grant/preview — who of the pasted lines is found (no changes) */
export const zAdminGrantPreview = z.object({ targets: z.array(zGrantTarget).min(1).max(ADMIN_GRANT_MAX_USERS) }).strict();
export type AdminGrantPreviewInput = z.input<typeof zAdminGrantPreview>;

/**
 * POST /api/admin/grant — credit `amount` to every found player (ledger `admin_adjust`, one transaction per player,
 * key `admin-grant:<batchId>:<userId>`): a repeat with the same batchId credits nobody twice.
 */
export const zAdminGrant = z
  .object({
    batchId: zIdempotencyKey,
    targets: z.array(zGrantTarget).min(1).max(ADMIN_GRANT_MAX_USERS),
    amount: z.number().int().min(1).max(ADMIN_GRANT_MAX_AMOUNT),
    comment: zAdminComment,
  })
  .strict();
export type AdminGrantInput = z.input<typeof zAdminGrant>;

export interface AdminGrantTarget {
  id: string;
  publicId: number;
  nickname: string;
  email: string | null;
  balance: number;
  /** the lines of the request that resolved to this player */
  inputs: string[];
}

export interface AdminGrantPreview {
  found: AdminGrantTarget[];
  /** lines that matched nobody (or an erased account) */
  notFound: string[];
}

export interface AdminGrantResult {
  batchId: string;
  amount: number;
  /** players credited by this request */
  granted: number;
  /** players already credited by an earlier request with the same batchId */
  duplicates: number;
  /** `balance` = after the grant */
  items: Array<AdminGrantTarget & { status: 'granted' | 'duplicate' }>;
  notFound: string[];
}

/* ---------------- grant to active players («Начислить всем активным за N дней») ---------------- */

/** players per «active players» batch: more → GRANT_TOO_MANY (narrow the filter) */
export const ADMIN_GRANT_ACTIVE_MAX_USERS = 5_000;
/** the longest look-back of «active players», days */
export const ADMIN_GRANT_ACTIVE_MAX_DAYS = 90;
/** players shown in the preview of an «active players» batch */
export const ADMIN_GRANT_ACTIVE_SAMPLE = 20;

/**
 * «Active players»: at least `minBets` stakes (amount > 0, not refunded) in the last `days` days; not banned, not
 * deleted, not administrators.
 */
export const zAdminGrantActiveFilter = z
  .object({
    days: z.number().int().min(1).max(ADMIN_GRANT_ACTIVE_MAX_DAYS),
    minBets: z.number().int().min(1).max(1_000_000),
  })
  .strict();
export type AdminGrantActiveFilter = z.infer<typeof zAdminGrantActiveFilter>;

/** POST /api/admin/grant/active/preview → AdminGrantActivePreview (no changes) */
export const zAdminGrantActivePreview = zAdminGrantActiveFilter;

export interface AdminGrantActivePreview extends AdminGrantActiveFilter {
  /** players matching the filter now */
  count: number;
  /** ADMIN_GRANT_ACTIVE_MAX_USERS: `count` above it cannot be granted in one batch */
  max: number;
  /** the first players (by public ID), up to ADMIN_GRANT_ACTIVE_SAMPLE */
  sample: AdminGrantTarget[];
}

/**
 * POST /api/admin/grant/active — credit `amount` to every active player (the server collects them) through the same
 * batch as the list grant: key `admin-grant:<batchId>:<userId>`, audit `balance_grant` + `balance_adjust` per player.
 * The window ends at the batch's first request: a repeat with the same batchId sees the same players (minus those
 * banned / deleted since) and credits nobody twice. More than ADMIN_GRANT_ACTIVE_MAX_USERS players → GRANT_TOO_MANY.
 */
export const zAdminGrantActive = zAdminGrantActiveFilter
  .extend({
    batchId: zIdempotencyKey,
    amount: z.number().int().min(1).max(ADMIN_GRANT_MAX_AMOUNT),
    comment: zAdminComment,
  })
  .strict();
export type AdminGrantActiveInput = z.input<typeof zAdminGrantActive>;

/** POST /api/admin/users/:id/ban */
export const zAdminBan = z.object({ banned: z.boolean(), comment: zAdminComment }).strict();

/* ---------------- game configs ---------------- */

const zBetBounds = {
  minBet: z.number().int().min(1).max(1_000_000),
  maxBet: z.number().int().min(1).max(1_000_000_000),
};

/**
 * Editable runtime params per game (GameConfig overrides, applied through getGameConfig).
 * - crash/pot/roulette: bet limits; crash also house edge (1–6 % → RTP 94–99 %), max multiplier, max win.
 * - slots: none — their RTP is fixed by the paytable/reel strips verified by the simulator.
 * - carp: bet limits (its RTP is fixed by the multiplier table in `@tg/games/carp`).
 * - firecrackers: bet limits (its RTP is fixed by the multipliers in `@tg/games/firecrackers`).
 * - hongbao: none — the stake is fixed per table (18 / 88 / 888) and the fee is part of the verified math.
 * - mahjong: bet limits (its RTP is fixed by the paytable in `@tg/games/mahjong` under the reference strategy).
 * - mahjong-table: none — the stake is fixed per table (18 / 88 / 888) and the 5 % fee is part of the rules.
 * - zhajinhua: Ante / Pair Plus limits (its RTP is fixed by the pay table in `@tg/games/zhajinhua`).
 * - doudizhu: none — tiers, cap and the 5 % fee are part of the rules (DDZ_CONFIG).
 * - tile-match / color-sort / daily-words / chess: none — entries, stakes and fees are rules (catalog.ts, skill.ts).
 * Pot/Roulette house edge is part of the verified math (fee / green) and is not editable at runtime.
 */
export const ADMIN_GAME_PARAMS: Record<GameId, readonly string[]> = {
  crash: ['minBet', 'maxBet', 'houseEdge', 'maxMultiplier', 'maxWin'],
  pot: ['minBet', 'maxBet'],
  roulette: ['minBet', 'maxBet'],
  garden: [],
  deep: [],
  spaceport: [],
  'sweet-shop': [],
  workshop: [],
  'koi-pond': [],
  'lucky-lever': [],
  'neon-arcade': [],
  'pirate-cove': [],
  carp: ['minBet', 'maxBet'],
  firecrackers: ['minBet', 'maxBet'],
  hongbao: [],
  mahjong: ['minBet', 'maxBet'],
  'mahjong-table': [],
  zhajinhua: ['minBet', 'maxBet'],
  doudizhu: [],
  // skill games: entries / stakes and fees are part of the rules (catalog.ts, skill.ts SKILL_CONTEST_CONFIG)
  'tile-match': [],
  'color-sort': [],
  'daily-words': [],
  chess: [],
};

export function zGameConfigFor(game: GameId) {
  if ((ADMIN_GAME_PARAMS[game] ?? []).length === 0) return z.object({}).strict();
  const base = game === 'crash'
    ? z
        .object({
          ...zBetBounds,
          houseEdge: z.number().min(0.01).max(0.06),
          maxMultiplier: z.number().int().min(10).max(1_000_000),
          maxWin: z.number().int().min(1_000).max(1_000_000_000),
        })
        .partial()
        .strict()
    : z.object(zBetBounds).partial().strict(); // pot, roulette, carp, firecrackers, mahjong, zhajinhua
  return base.refine((c: { minBet?: number; maxBet?: number }) => c.minBet === undefined || c.maxBet === undefined || c.minBet <= c.maxBet, {
    path: ['maxBet'],
    message: 'maxBet must be ≥ minBet',
  });
}

/** PUT /api/admin/games/:game/config — the whole override object (omitted keys fall back to defaults) */
export const zAdminGameConfig = z.object({ config: z.record(z.unknown()), comment: zAdminComment }).strict();

export interface AdminGameConfig {
  game: GameId;
  params: readonly string[];
  /** effective values (defaults + override) */
  effective: Record<string, number>;
  /** the override row only */
  override: Record<string, number>;
  /** catalog.ts: house — against the house (target RTP); pvp / pool — the fee is the margin */
  economy: GameEconomy;
  /** percent: the RTP of a house game, 100 − fee of a pvp / pool game (what the players get back in total) */
  targetRtp: number;
  updatedAt: string | null;
}

/* ---------------- RTP report ---------------- */

export const ADMIN_RTP_PERIODS = ['day', 'week'] as const;
export type AdminRtpPeriod = (typeof ADMIN_RTP_PERIODS)[number];

export interface AdminRtpRow {
  game: GameId;
  bets: number;
  staked: number;
  paidOut: number;
  /** Σpayout / Σstake, null if nothing staked */
  rtp: number | null;
  economy: GameEconomy;
  /** percent: the RTP of a house game, 100 − fee of a pvp / pool game */
  targetRtp: number;
  /**
   * From the house journal over the period (by entry time): games against the house — stakes − payouts − refunds;
   * shared-bank games (Pot, Red Envelopes) — Σ fees.
   */
  houseIncome: number;
}

/** House wallet (owner decision №3): Σ players + house + pool = 0. */
export interface AdminHouseSummary {
  /** house account, all time since the house ledger was introduced; may be negative */
  balance: number;
  /** stakes of open shared-bank rounds */
  pool: number;
  /** the shared jackpot's pool (house account `jackpot`) */
  jackpot: number;
  /** Σ player balances */
  players: number;
  /** players + balance + pool + jackpot == 0 */
  zeroSum: boolean;
  /** Δ of the house account over the period = gameIncome − issued */
  income: number;
  /** margin of games + shared-bank fees over the period */
  gameIncome: number;
  /** tokens issued over the period: bonuses, prizes, admin adjustments (net, positive = issued) */
  issued: number;
  /** stakes accepted over the period */
  turnover: number;
}

/** GET /api/admin/rtp?period=day|week */
export interface AdminRtpReport {
  period: AdminRtpPeriod;
  since: string;
  rows: AdminRtpRow[];
  house: AdminHouseSummary;
}

/* ---------------- audit ---------------- */

/** every audit action the server writes (the filter list; unknown ones from older versions are still shown) */
export const ADMIN_ACTIONS = [
  'balance_adjust',
  'balance_grant',
  'ban',
  'unban',
  'deletion_cancel',
  'game_config',
  'season_toggle',
  'currency_draft',
  'currency_publish',
  'tournament_create',
  'tournament_delete',
  'tourney_create',
  'tourney_cancel',
  'chat_hide',
  'chat_dismiss',
  'award_revoke',
  'export_csv',
  'nickname_reset',
] as const;
export type AdminActionType = (typeof ADMIN_ACTIONS)[number];

const zInstant = z.string().datetime({ offset: true });

/**
 * GET /api/admin/audit — filters: who acted (`adminId`), what (`action`), on whom (`userId` — exact, or `player` —
 * part of the nickname / e-mail, `#123`), when (`from` ≤ createdAt < `to`, ISO instants: the admin's local day borders).
 */
export const zAdminAuditQuery = z
  .object({
    page: zPage,
    adminId: z.string().uuid().optional(),
    action: z.enum(ADMIN_ACTIONS).optional(),
    userId: z.string().uuid().optional(),
    player: z.string().trim().min(1).max(100).optional(),
    from: zInstant.optional(),
    to: zInstant.optional(),
  })
  .strict();
export type AdminAuditFilter = Omit<z.infer<typeof zAdminAuditQuery>, 'page'>;

/** GET /api/admin/audit/admins — who can be picked in the «Admin» filter (admins now + anyone with audit rows) */
export interface AdminActor {
  id: string;
  nickname: string;
}

export interface AdminActionRow {
  id: string;
  admin: string;
  target: string | null;
  targetId: string | null;
  action: string;
  comment: string;
  data: unknown;
  createdAt: string;
}

export const ADMIN_GAMES = GAME_IDS;

/* ---------------- player card: bets, seeds, limits ---------------- */

/** GET /api/admin/users/:id/bets?page=&game= — the player's bets, newest first (the same rows as the profile history) */
export const zAdminBetsQuery = z.object({ page: zPage, game: z.enum(HISTORY_GAME_FILTERS).optional() }).strict();
export type AdminBetRow = BetHistoryItem;

/** one seed pair of the player: the active one (server seed secret) or a rotated one (server seed revealed) */
export interface AdminSeedRow {
  id: string;
  serverSeedHash: string;
  /** null while active: never shown before the rotation, to admins neither */
  serverSeed: string | null;
  clientSeed: string;
  /** active: the next nonce; rotated: the nonces used (0 … nonce − 1) */
  nonce: number;
  createdAt: string;
  revealedAt: string | null;
}

/** GET /api/admin/users/:id/seeds?page= — the active pair and the rotation history (newest first) */
export interface AdminSeeds {
  active: AdminSeedRow | null;
  history: Paged<AdminSeedRow>;
}

/**
 * GET /api/admin/users/:id/limits — read-only: the responsible-play rules say a break cannot be cancelled or shortened and
 * a raised limit applies after 24 h — by the player too, so an admin cannot lift them either.
 */
export interface AdminLimits extends LimitsState {
  updatedAt: string | null;
}

/* ---------------- CSV exports ---------------- */

/** GET /api/admin/export/<kind>.csv — rows per file at most (newest first) */
export const ADMIN_EXPORT_MAX_ROWS = 50_000;
export const ADMIN_EXPORT_KINDS = ['users', 'ledger', 'audit', 'rtp'] as const;
export type AdminExportKind = (typeof ADMIN_EXPORT_KINDS)[number];
export const zAdminExportUsers = z.object({ q: z.string().trim().max(100).optional() }).strict();
export const zAdminExportAudit = zAdminAuditQuery.omit({ page: true });
export const zAdminExportRtp = z.object({ period: z.enum(['day', 'week']).default('day') }).strict();

/* ---------------- holiday decorations switch ---------------- */

/** GET /api/admin/season and the answer of PUT /api/admin/season. `current` = by the calendar, ignoring the switch. */
export interface AdminSeasonState {
  enabled: boolean;
  current: Season | null;
}

/** PUT /api/admin/season */
export const zAdminSeason = z.object({ enabled: z.boolean(), comment: zAdminComment }).strict();
export type AdminSeasonInput = z.infer<typeof zAdminSeason>;

/* ---------------- chat moderation (reports) ---------------- */

/** `open` — waiting for a moderator; `closed` — resolved (message hidden) or dismissed */
export const ADMIN_CHAT_REPORT_FILTERS = ['open', 'closed'] as const;
export type AdminChatReportFilter = (typeof ADMIN_CHAT_REPORT_FILTERS)[number];

/** GET /api/admin/chat/reports?status=open|closed&page= */
export const zAdminChatReportsQuery = z.object({ status: z.enum(ADMIN_CHAT_REPORT_FILTERS).default('open'), page: zPage });

export const ADMIN_CHAT_ACTIONS = ['hide', 'dismiss'] as const;
export type AdminChatAction = (typeof ADMIN_CHAT_ACTIONS)[number];

/**
 * POST /api/admin/chat/reports/:id/resolve — `hide`: the message gets `hiddenAt` and every open report on it is resolved
 * (audit `chat_hide`); `dismiss`: this report is dismissed (audit `chat_dismiss`). A repeat on a closed report changes nothing.
 */
export const zAdminChatResolve = z.object({ action: z.enum(ADMIN_CHAT_ACTIONS), comment: zAdminComment }).strict();
export type AdminChatResolveInput = z.infer<typeof zAdminChatResolve>;

export interface AdminChatUser {
  id: string;
  publicId: number;
  nickname: string;
  bannedAt: string | null;
}

/** a message as the moderator sees it: the original text even when hidden */
export interface AdminChatMessage {
  id: string;
  /** internal user id of the sender (= author or recipient of the conversation) */
  fromId: string;
  text: string;
  hiddenAt: string | null;
  createdAt: string;
}

export interface AdminChatReport {
  id: string;
  reason: ChatReportReason;
  note: string | null;
  status: ChatReportStatus;
  createdAt: string;
  resolvedAt: string | null;
  /** moderator's nickname */
  resolvedBy: string | null;
  message: AdminChatMessage;
  /** the reported message's sender (ban goes to the player card) */
  author: AdminChatUser;
  /** the other side of the conversation */
  recipient: AdminChatUser;
  reporter: AdminChatUser;
  /** the conversation of that pair around the reported message (up to CHAT_REPORT_CONTEXT before and after), oldest first, the reported one included */
  context: AdminChatMessage[];
  /** open reports on the same message (this one included) */
  openOnMessage: number;
}

/** GET /api/admin/chat/reports — `open`: the number of open reports (the tab badge), whatever the filter */
export interface AdminChatReports extends Paged<AdminChatReport> {
  open: number;
}

/** POST /api/admin/chat/reports/:id/resolve — `changed: false` when the report was already closed (a repeat) */
export interface AdminChatResolveResult {
  changed: boolean;
  /** reports closed by this action */
  closed: number;
  open: number;
}
